import React from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, LineChart, Line, Brush
} from 'recharts';
import { BarChart3 } from 'lucide-react';

// ============================================
// Кастомный Tooltip с реальными датами
// ============================================
const ComparisonTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    const row = payload[0]?.payload || {};

    return (
        <div className="bg-slate-900 border border-slate-600 rounded-lg p-3 shadow-xl text-xs">
            {/* Заголовок: даты обоих периодов */}
            <div className="text-slate-400 font-semibold mb-2 border-b border-slate-700 pb-1.5 space-y-0.5">
                {row.currentDate && (
                    <div className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block" />
                        <span className="text-blue-300">{row.currentDate}</span>
                    </div>
                )}
                {row.previousDate && (
                    <div className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-slate-500 inline-block" />
                        <span className="text-slate-400">{row.previousDate}</span>
                    </div>
                )}
                {!row.currentDate && !row.previousDate && (
                    <div>{label}</div>
                )}
            </div>

            {/* Значения */}
            {payload.map((entry, idx) => (
                <div key={idx} className="flex items-center gap-2 mb-1">
                    <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: entry.stroke || entry.fill }}
                    />
                    <span className="text-slate-300">{entry.name}:</span>
                    <span className="text-white font-mono font-semibold">
                        {entry.value != null ? Number(entry.value).toFixed(3) : '—'}
                    </span>
                </div>
            ))}

            {/* Разница */}
            {row.current != null && row.previous != null && (
                <div className="mt-1.5 pt-1.5 border-t border-slate-700 text-slate-400">
                    Δ = <span className={`font-semibold ${row.current - row.previous >= 0 ? 'text-red-400' : 'text-blue-400'}`}>
                        {(row.current - row.previous) >= 0 ? '+' : ''}
                        {(row.current - row.previous).toFixed(3)}
                    </span>
                </div>
            )}
        </div>
    );
};

// ============================================
// Основной компонент
// ============================================
const ComparisonChart = ({ title, data, unit = '', chartType = 'bar' }) => {
    if (!data || data.length === 0) {
        return (
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-8 text-center text-slate-500">
                <BarChart3 className="w-10 h-10 mx-auto mb-2" />
                Нет данных для сравнения
            </div>
        );
    }

    // ✅ Интервал подписей: при >15 точках показываем каждую 3-ю, при >30 — каждую 5-ю
    const tickInterval = data.length > 30 ? 4 : data.length > 15 ? 2 : 0;

    return (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
            <div className="px-4 py-3 bg-slate-700/50 border-b border-slate-700">
                <h4 className="text-white font-semibold text-sm flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-blue-400" />
                    {title}
                    {unit && <span className="text-xs text-slate-400 font-normal">({unit})</span>}
                </h4>
            </div>
            <div className="p-4">
                <ResponsiveContainer width="100%" height={320}>
                    {chartType === 'bar' ? (
                        <BarChart data={data} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                            <XAxis
                                dataKey="label"
                                stroke="#475569"
                                tick={{ fontSize: 10, fill: '#94a3b8' }}
                                interval={tickInterval}
                            />
                            <YAxis
                                stroke="#475569"
                                tick={{ fontSize: 10, fill: '#94a3b8' }}
                            />
                            <Tooltip content={<ComparisonTooltip />} />
                            <Legend wrapperStyle={{ fontSize: '11px', color: '#94a3b8' }} />
                            <Bar dataKey="current" name="Текущий период" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="previous" name="Прошлый период" fill="#64748b" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    ) : (
                        <LineChart data={data} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                            <XAxis
                                dataKey="label"
                                stroke="#475569"
                                tick={{ fontSize: 10, fill: '#94a3b8' }}
                                interval={tickInterval}
                            />
                            <YAxis
                                stroke="#475569"
                                tick={{ fontSize: 10, fill: '#94a3b8' }}
                            />
                            <Tooltip content={<ComparisonTooltip />} />
                            <Legend wrapperStyle={{ fontSize: '11px', color: '#94a3b8' }} />
                            <Line
                                type="monotone"
                                dataKey="current"
                                name="Текущий период"
                                stroke="#3b82f6"
                                strokeWidth={2}
                                dot={data.length <= 31}
                                activeDot={{ r: 5, strokeWidth: 2 }}
                                connectNulls={false}
                                isAnimationActive={false}
                            />
                            <Line
                                type="monotone"
                                dataKey="previous"
                                name="Прошлый период"
                                stroke="#64748b"
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                dot={false}
                                activeDot={{ r: 4, strokeWidth: 2 }}
                                connectNulls={false}
                                isAnimationActive={false}
                            />
                            <Brush
                                dataKey="label"
                                height={20}
                                stroke="#3b82f6"
                                fill="#1e293b"
                                travellerWidth={8}
                            />
                        </LineChart>
                    )}
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default ComparisonChart;